import { config } from './config';
import { supabase } from './supabase';

/* Báo lỗi tự động về Worker (đường dẫn /log): chỉ ghi nhật ký, xem bằng `npx wrangler tail aop-api` hoặc dashboard Cloudflare.
 * Không bao giờ ném lỗi, không chặn giao diện, không gửi dữ liệu nghiệp vụ (chỉ thông báo lỗi, vết gọi hàm, địa chỉ trang). */

const MAX_PER_SESSION = 15;      // một phiên mở trang gửi tối đa chừng này báo lỗi
const DEDUPE_MS = 30_000;        // cùng một lỗi trong 30 giây chỉ gửi một lần
const IGNORE = [/ResizeObserver loop/i, /^Script error\.?$/i, /Non-Error promise rejection captured/i];

let sent = 0;
const recent = new Map<string, number>();

/** Chỉ dùng cho test. */
export function resetErrorReporting(): void { sent = 0; recent.clear(); }

const text = (e: unknown): { message: string; stack: string } => {
  if (e instanceof Error) return { message: `${e.name}: ${e.message}`, stack: e.stack || '' };
  if (typeof e === 'string') return { message: e, stack: '' };
  try { return { message: JSON.stringify(e), stack: '' }; } catch { return { message: String(e), stack: '' }; }
};

export function reportError(kind: 'js' | 'promise' | 'react' | 'api' | 'query', err: unknown, context = ''): void {
  try {
    if (!config.apiUrl || sent >= MAX_PER_SESSION) return;
    const { message, stack } = text(err);
    if (!message || IGNORE.some(r => r.test(message))) return;
    const key = `${kind}|${message}`, now = Date.now();
    if ((recent.get(key) ?? 0) > now - DEDUPE_MS) return;
    recent.set(key, now); sent++;
    const body = JSON.stringify({ kind, message, stack, context, url: typeof location !== 'undefined' ? location.href : '' });
    void (async () => {
      let auth = '';
      try { const { data } = await supabase().auth.getSession(); if (data.session) auth = `Bearer ${data.session.access_token}`; } catch { /* chưa đăng nhập */ }
      await fetch(`${config.apiUrl}/log`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: auth } : {}) }, body });
    })().catch(() => { /* mất mạng: bỏ qua */ });
  } catch { /* báo lỗi không được phép gây lỗi */ }
}

/** Gắn bộ bắt lỗi toàn cục (lỗi JavaScript và lời hứa bị từ chối không ai xử lý). Trả về hàm gỡ. */
export function installGlobalErrorReporting(): () => void {
  const onError = (ev: ErrorEvent) => reportError('js', ev.error ?? ev.message, ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : '');
  const onRejection = (ev: PromiseRejectionEvent) => reportError('promise', ev.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => { window.removeEventListener('error', onError); window.removeEventListener('unhandledrejection', onRejection); };
}

/** Lỗi do truy vấn dữ liệu: bỏ qua lỗi người dùng (thiếu quyền, hết phiên) và lỗi mạng, chỉ báo lỗi bất thường. */
export function reportQueryError(err: unknown, key: unknown): void {
  const e = err as { code?: string; message?: string; status?: number } | null;
  const msg = e?.message ?? '';
  if (e?.code === '42501' || e?.code === 'PGRST301' || e?.status === 401 || e?.status === 403 || /JWT|Failed to fetch|NetworkError|network|Load failed/i.test(msg)) return;
  reportError('query', err, `truy vấn ${Array.isArray(key) ? String(key[0]) : String(key)}`);
}
